import { eq } from "drizzle-orm";
import { Box } from "@mui/material";
import type React from "react";
import { useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

// @ts-ignore
import type { SearchMessageResult } from "@storyboard/admin-client";
import PodcastItem from "@/components/Search/PodcastItem";
import { db } from "@/db/db";
import { feed as feedTable } from "@/db/schema";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { useReactiveRelationalQuery } from "@/models/hooks/useReactiveRelationalQuery.ts";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UxContext } from "@/models/UxStateProvider";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";
import SearchItemHeader from "./SearchItemHeader";

function getFeedRecord(feedId?: string) {
  const query = db.query.feed.findFirst({
    where: eq(feedTable.id, feedId),
  });
  return feedId ? query : (emptyQuery as unknown as typeof query);
}

export default function MessageItem({
  active,
  activeLinkIndex,
  id,
  itemIndex,
  lastItem,
  message,
}: {
  active: boolean;
  activeLinkIndex: number;
  id: string;
  itemIndex: number;
  lastItem: boolean;
  message: SearchMessageResult;
}) {
  const { setGlobalSearchOpen } = useContext(UxContext);
  const { ampli } = useContext(TrackingContext);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const feedRecord = useReactiveRelationalQuery(getFeedRecord, [message.feedId], "MessageItem.getFeed");
  const feedPath = feedRecord?.aliasMembershipId
    ? `/workspaces/${message.workspaceId}/drivers/${feedRecord.aliasMembershipId}/channel/${message.feedId}`
    : `/workspaces/${message.workspaceId}/channels/${message.feedId}`;
  const url = `${feedPath}?feedItemId=${message.feedItemId}`;
  const isPodcast = message?.displayTitle?.length > 0 || message?.displayDescription?.length > 0;
  const isPodcastLink = isPodcast && "pod-cast";
  const activeLink = active && "active";
  const lastItemLink = lastItem && "last-item";
  const linkClassName = `search-item ${isPodcastLink} ${activeLink} ${lastItemLink}`;
  const navigate = useNavigate();

  const clickEvent = async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e) {
      e.preventDefault();
    }
    setGlobalSearchOpen(() => false);
    ampli.searchLinkToMessage({ workspaceId: message.workspaceId });
    navigate(url);
  };

  /**
   * Keyboard enter event handler
   */
  useEffect(() => {
    if (active && activeLinkIndex === itemIndex && linkRef.current) {
      clickEvent().then(() => {
        navigate(url);
      });
    }
  }, [active, activeLinkIndex, active, itemIndex, clickEvent]);

  return (
    <Link ref={linkRef} id={id} to={url} className={linkClassName} onClick={(e) => clickEvent(e)}>
      <Box>
        <SearchItemHeader message={message} />
        {isPodcast && <PodcastItem message={message} />}
        <Box
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForDisplay(message?.fullTranscription ?? ""),
          }}
        />
      </Box>
    </Link>
  );
}
